// The one horizontal rail controller on this storefront.
//
// The mechanism -- scroll the row, page it by whole items, show the arrows
// only while it actually overflows, and report the position on a mobile
// track -- had been written twice before this file existed (the collection
// page's category band and the home page's tabbed product rail). The home
// page's last slice needs it twice more, so it lives here once instead of
// four times.
//
// Contract, every part optional except the row: a rail with no arrows and no
// track still gets overflow detection, which is what drives the CSS.
//
//   [data-kludi-rail-row]    the scrolling element
//   [data-kludi-rail-item]   one item, used to derive the paging distance
//   [data-kludi-rail-prev]   [data-kludi-rail-next]   paging buttons
//   [data-kludi-rail-thumb]  the mobile scroll-position thumb
//   data-rail-page="2"       items travelled per arrow press (default 1)
//
// Two rails do not have to agree on their numbers, so nothing measured is
// baked in here: the paging distance comes from the rail's own first item and
// its own column gap, never from a pixel figure copied between components.
class KludiRail extends HTMLElement {
  connectedCallback() {
    this.initRail();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onRailResize);
  }

  // separate from connectedCallback so a subclass can set its own state up
  // first and then start the rail, rather than having to call super and hope
  // the ordering works out
  initRail() {
    this.prevButton = this.querySelector('[data-kludi-rail-prev]');
    this.nextButton = this.querySelector('[data-kludi-rail-next]');
    this.thumb = this.querySelector('[data-kludi-rail-thumb]');

    this._onRailScroll = this._onRailScroll.bind(this);
    this._onRailResize = this._onRailResize.bind(this);

    // every row, not just the visible one: the tabbed rail keeps one row per
    // panel and swaps which of them is on show
    this.querySelectorAll('[data-kludi-rail-row]').forEach((row) => {
      row.addEventListener('scroll', this._onRailScroll, { passive: true });
    });

    this.prevButton?.addEventListener('click', () => this.page(-1));
    this.nextButton?.addEventListener('click', () => this.page(1));

    window.addEventListener('resize', this._onRailResize);

    this.refreshRail();
  }

  // a getter rather than a stored element, because which row is the live one
  // can change without the DOM changing
  get railRow() {
    return this.querySelector('[data-kludi-rail-row]');
  }

  get railPageBy() {
    return parseInt(this.dataset.railPage, 10) || 1;
  }

  // call after anything that changes what the rail holds or how wide it is
  refreshRail() {
    this._updateRailOverflow();
    this._onRailScroll();
  }

  _onRailResize() {
    this.refreshRail();
  }

  _updateRailOverflow() {
    const row = this.railRow;
    const overflowing = row ? row.scrollWidth > row.clientWidth + 1 : false;
    this.toggleAttribute('data-overflowing', overflowing);

    /*
      the arrows go disabled rather than disappearing. the design shows both
      of them on every home-page rail unconditionally -- only the collection
      page's category band is drawn conditionally there, and that one has its
      own controller. hiding them here meant the tabbed rail dropped its
      controls the moment a short tab was selected: "Coming soon" holds two
      products, which fit without scrolling, so switching to it made the pair
      vanish mid-interaction
    */
    this._updateRailArrows(overflowing);
  }

  /*
    each arrow reports its own direction: greyed at the end of its travel, not
    only when the whole rail fits. an arrow that looks live while the rail is
    already at that end is a control that does nothing, which is exactly what
    the disabled treatment exists to say
  */
  _updateRailArrows(overflowing) {
    const row = this.railRow;
    if (!row) return;

    const atStart = row.scrollLeft <= 1;
    const atEnd = row.scrollLeft >= row.scrollWidth - row.clientWidth - 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }

  // travels a whole number of items, so the rail always lands on an item edge
  page(direction) {
    const row = this.railRow;
    const item = row ? row.querySelector('[data-kludi-rail-item]') : null;
    if (!row || !item) return;

    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const pitch = item.getBoundingClientRect().width + gap;
    row.scrollBy({ left: direction * pitch * this.railPageBy, behavior: 'smooth' });
  }

  _onRailScroll() {
    const row = this.railRow;
    if (!row) return;

    // the arrows follow the scroll position, not only the initial measurement
    this._updateRailArrows(row.scrollWidth > row.clientWidth + 1);

    if (!this.thumb) return;

    const { scrollLeft, scrollWidth, clientWidth } = row;
    const maxScroll = scrollWidth - clientWidth;
    const trackWidth = this.thumb.parentElement.clientWidth;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 24);
    const maxThumbTravel = trackWidth - thumbWidth;
    const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;

    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${progress * maxThumbTravel}px)`;
  }
}

// published on window so the subclasses in the other rail assets can extend
// it -- they are plain classic scripts, not modules
window.KludiRail = KludiRail;

window.customElements.define('kludi-rail', KludiRail);
